import React from "react";
import { Form, FormikProvider, useFormik } from "formik";

import { useUpdateEntityAddressMutation } from "@api/entity.api";
import { useEntity } from "@components/entity/useEntity";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { AddressForm } from "@components/forms/AddressForm";
import { getFullAddressFormSchema } from "@components/forms/AddressForm.lib";
import { Address } from "@common/types/embeddable/address";
import { EditModalFooter } from "./EditModalFooter";

interface AddressFormValues {
    street: string;
    street2: string;
    city: string;
    state: string;
    postalCode: string;
}

function getRelevantAddressFields(
    address: Partial<Address>,
): AddressFormValues {
    return {
        street: address.street ?? "",
        street2: address.street2 ?? "",
        city: address.city ?? "",
        state: address.state ?? "",
        postalCode: address.postalCode ?? "",
    };
}

interface Props {
    show: boolean;
    onClose(): void;
    address?: Partial<Address>;
}

export const UpdateAddressModal: React.FC<Props> = ({
    show,
    onClose,
    address,
}) => {
    const entity = useEntity();
    const mutation = useUpdateEntityAddressMutation(entity);
    const { error } = useToaster();

    const schema = getFullAddressFormSchema(!entity.isPersonal);

    const formik = useFormik<AddressFormValues>({
        initialValues: getRelevantAddressFields(address ?? {}),
        enableReinitialize: true,
        validate: validateFormikWithZodSchema(schema),
        onSubmit: submitHelper({
            handler: async (newAddress) => {
                try {
                    await mutation.mutateAsync({
                        street: newAddress.street,
                        street2: newAddress.street2 || null,
                        city: newAddress.city,
                        state: newAddress.state,
                        postalCode: newAddress.postalCode,
                    });
                    onClose();
                } catch {
                    error("Failed to update profile");
                }
            },
        }),
    });

    return (
        <PlainModal show={show} onHide={onClose}>
            <StandardModalHeader>Update address</StandardModalHeader>
            <FormikProvider value={formik}>
                <Form>
                    <fieldset>
                        <AddressForm prefix="" fieldSize="sm" includeStreet2 />
                    </fieldset>

                    <EditModalFooter
                        onClose={onClose}
                        isValid={formik.isValid}
                        isSubmitting={formik.isSubmitting}
                    />
                </Form>
            </FormikProvider>
        </PlainModal>
    );
};
